import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useHotkeys} from "react-hotkeys-hook";
import PropTypes from "prop-types";
import {Autocomplete, Dialog, Paper, TextField, Typography} from "@mui/material";
import {useFeatureFlags} from "../../context/FeatureFlagProvider";
import {usePowerbarStyles} from "./PowerbarStyles";
import {usePowerbarItems} from "./PowerbarItems";
import {find, omit, pluck} from "underscore";

const Powerbar = ({state}) => {
  const navigate = useNavigate();
  const {classes} = usePowerbarStyles();
  const featureFlags = useFeatureFlags();
  const powerbarItems = usePowerbarItems();

  const [count, setCount] = useState(0);

  useEffect(() => {
    if (count > 2) {
      featureFlags.setFeatureFlags((prevFlags) => ({
        ...prevFlags,
        showDeveloper: !prevFlags.showDeveloper,
        showStaleDetectionWithoutLiveChanges: !prevFlags.showStaleDetectionWithoutLiveChanges,
        setFeatureFlags: prevFlags.setFeatureFlags,
      }));
      setCount(0);
    }
  }, [count]);

  useHotkeys("ctrl+alt+p", () => state.setOpen());
  useHotkeys("ctrl+alt+shift+`", () => setCount((prevCount) => prevCount + 1));

  const handleChange = (event, value) => {
    state.setClosed();
    navigate(value.path);
  };

  const keys = pluck(powerbarItems, "key").join();

  useHotkeys(keys, (_keyboardEvent, hotkeyEvent) => {
    const pbi = find(powerbarItems, (pbi) => pbi.key === hotkeyEvent.key);
    if (pbi) {
      handleChange(null, pbi);
    }
  });

  return (
    <Dialog open={state.open} onClose={() => state.setClosed()} className={classes.powerbar}>
      <Autocomplete
        open
        autoHighlight
        options={powerbarItems}
        getOptionLabel={(option) => option.action || ""}
        isOptionEqualToValue={(option, value) => {
          return option && value && option?.key === value?.key;
        }}
        onChange={handleChange}
        clearOnEscape
        size="small"
        style={{width: 300}}
        renderOption={(props, option) => (
          <li key={props?.key} {...omit(props, "key")}>
            <span style={{width: "100%"}}>
              <Typography component="span" variant="body2">
                {option.action}
              </Typography>
              <Typography variant="caption" style={{float: "right"}}>
                (
                {option.key}
                )
              </Typography>
            </span>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            // TextField traps all keystrokes and therefore blocks Escape from closing the powerbar
            onKeyUp={(e) => e.key === "Escape" && state.setClosed()}
            label=""
            variant="outlined"
          />
        )}
        PaperComponent={(params) => <Paper square {...params} />}
        ListboxProps={{style: {lineHeight: 1, maxHeight: "unset"}}}
      >
      </Autocomplete>
    </Dialog>
  );
};

Powerbar.propTypes = {
  state: PropTypes.shape({
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    setClosed: PropTypes.func.isRequired,
  }),
};

export default Powerbar;

export const usePowerbar = () => {
  const [powerBar, setPowerBar] = useState(false);
  return {
    open: powerBar,
    setOpen: () => setPowerBar(true),
    setClosed: () => setPowerBar(false),
  };
};
